'use client';
import React, { useState, useEffect } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { Shield, Mail, Globe, Sun, Moon, AlertTriangle, Key, ShieldCheck, Zap, Eye, EyeOff, LogIn, UserCheck } from 'lucide-react';
import { toast } from 'sonner';

import { useAuth } from '@/contexts/AuthContext';

interface Props {
  lang: 'vi' | 'en';
  onLangChange: (l: 'vi' | 'en') => void;
  darkMode: boolean;
  onDarkModeChange: (v: boolean) => void;
}

const t = {
  vi: {
    tagline: 'Bảo vệ tài liệu doanh nghiệp với mã hóa AES-256',
    subtitle: 'Hệ thống IRM & DLP hàng đầu cho tổ chức giáo dục',
    domainOnly: 'Đăng nhập bằng Email và Mật khẩu',
    email: 'Email',
    emailPlaceholder: 'name@example.com',
    password: 'Mật khẩu',
    passwordPlaceholder: 'Nhập mật khẩu',
    loginBtn: 'Đăng nhập',
    loggingIn: 'Đang đăng nhập...',
    feature1: 'Mã hóa AES-256 tiêu chuẩn quân sự',
    feature2: 'Kiểm soát quyền truy cập chi tiết',
    feature3: 'Thu hồi quyền từ xa tức thì',
    feature4: 'Ngăn chặn rò rỉ dữ liệu DLP',
    emailRequired: 'Vui lòng nhập email',
    emailInvalid: 'Email không hợp lệ',
    passwordRequired: 'Vui lòng nhập mật khẩu',
    loginError: 'Email hoặc mật khẩu không đúng. Vui lòng thử lại.',
    domainRestrict: 'Hệ thống IRM & DLP — IRMVault',
    guestMode: 'Tiếp tục không đăng nhập',
    guestModeDesc: 'Truy cập với quyền hạn chế, không cần tài khoản',
    orDivider: 'hoặc',
    adminNote: 'Tài khoản do Super Admin tạo và cấp mật khẩu.',
    continueWithGoogle: 'Tiếp tục với Google',
    googleLoading: 'Đang chuyển hướng...',
    oauthErrors: {
      access_denied: 'Bạn đã từ chối đăng nhập với Google. Vui lòng thử lại.',
      no_code: 'Phiên xác thực không hợp lệ. Vui lòng thử lại.',
      auth_failed: 'Xác thực thất bại. Vui lòng thử lại.',
      invalid_client: 'Cấu hình Google OAuth không hợp lệ. Liên hệ quản trị viên.',
      redirect_uri_mismatch: 'Redirect URI không khớp. Liên hệ quản trị viên.',
      default: 'Đăng nhập Google thất bại. Vui lòng thử lại.',
    },
  },
  en: {
    tagline: 'Enterprise document protection with AES-256 encryption',
    subtitle: 'Leading IRM & DLP system for educational organizations',
    domainOnly: 'Sign in with Email and Password',
    email: 'Email',
    emailPlaceholder: 'name@example.com',
    password: 'Password',
    passwordPlaceholder: 'Enter your password',
    loginBtn: 'Sign In',
    loggingIn: 'Signing in...',
    feature1: 'Military-grade AES-256 encryption',
    feature2: 'Granular access permission control',
    feature3: 'Instant remote permission revocation',
    feature4: 'Data Loss Prevention (DLP) engine',
    emailRequired: 'Please enter your email',
    emailInvalid: 'Invalid email address',
    passwordRequired: 'Please enter your password',
    loginError: 'Incorrect email or password. Please try again.',
    domainRestrict: 'IRM & DLP Platform — IRMVault',
    guestMode: 'Continue without signing in',
    guestModeDesc: 'Access with limited permissions, no account needed',
    orDivider: 'or',
    adminNote: 'Accounts are created and managed by the Super Admin.',
    continueWithGoogle: 'Continue with Google',
    googleLoading: 'Redirecting...',
    oauthErrors: {
      access_denied: 'You denied Google sign-in. Please try again.',
      no_code: 'Invalid authentication session. Please try again.',
      auth_failed: 'Authentication failed. Please try again.',
      invalid_client: 'Invalid Google OAuth configuration. Contact your administrator.',
      redirect_uri_mismatch: 'Redirect URI mismatch. Contact your administrator.',
      default: 'Google sign-in failed. Please try again.',
    },
  },
};

// Google SVG icon (official colors)
function GoogleIcon({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </svg>
  );
}

export default function SignUpLoginContent({ lang, onLangChange, darkMode, onDarkModeChange }: Props) {
  const tr = t[lang];
  const { signInWithGoogle, signInWithPassword, enterGuestMode } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();

  const [loginError, setLoginError] = useState('');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);

  // Handle OAuth error params from callback redirect
  useEffect(() => {
    const error = searchParams?.get('error');
    if (!error) return;
    const errors = tr.oauthErrors as Record<string, string>;
    const msg = errors[error] ?? errors.default;
    setLoginError(msg);
  }, [searchParams, lang]);

  const validateEmail = (val: string): boolean => {
    if (!val) return false;
    return val.includes('@') && val.includes('.');
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError('');
    setPasswordError('');
    setLoginError('');
    let valid = true;
    if (!email) { setEmailError(tr.emailRequired); valid = false; }
    else if (!validateEmail(email)) { setEmailError(tr.emailInvalid); valid = false; }
    if (!password) { setPasswordError(tr.passwordRequired); valid = false; }
    if (!valid) return;
    setIsLoggingIn(true);
    try {
      await signInWithPassword(email, password);
      toast.success(lang === 'vi' ? 'Đăng nhập thành công!' : 'Signed in successfully!');
      router.push('/document-management-dashboard');
    } catch {
      setLoginError(tr.loginError);
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleGoogleLogin = async () => {
    setLoginError('');
    setIsGoogleLoading(true);
    try {
      await signInWithGoogle();
      // signInWithGoogle redirects the browser — no further action needed
    } catch {
      setLoginError(tr.oauthErrors.default);
      setIsGoogleLoading(false);
    }
  };

  const handleGuestMode = () => {
    document.cookie = 'guest_mode=1; path=/; max-age=86400; SameSite=Lax';
    enterGuestMode();
    router.push('/document-management-dashboard');
  };

  const features = [
    { icon: Shield, label: tr.feature1 },
    { icon: Key, label: tr.feature2 },
    { icon: Zap, label: tr.feature3 },
    { icon: ShieldCheck, label: tr.feature4 },
  ];

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between w-[480px] xl:w-[520px] flex-shrink-0 gradient-brand p-10 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />

        <div className="flex items-center gap-3 relative z-10">
          <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
            <Shield size={20} className="text-white" />
          </div>
          <div>
            <span className="font-800 text-xl text-white tracking-tight">IRMVault</span>
            <p className="text-white/70 text-[11px] font-500 tracking-wide">IRM & DLP Platform</p>
          </div>
        </div>

        <div className="relative z-10">
          <h1 className="text-3xl xl:text-4xl font-800 text-white leading-tight mb-4">{tr.tagline}</h1>
          <p className="text-white/75 text-base leading-relaxed mb-10">{tr.subtitle}</p>
          <div className="space-y-3">
            {features.map((f, i) => {
              const FeatureIcon = f.icon;
              return (
                <div key={`feature-${i}`} className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-white/15 rounded-lg flex items-center justify-center flex-shrink-0 backdrop-blur-sm">
                    <FeatureIcon size={15} className="text-white" />
                  </div>
                  <span className="text-white/85 text-sm font-500">{f.label}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="relative z-10 grid grid-cols-3 gap-4">
          {[
            { value: 'AES-256', label: 'Encryption' },
            { value: '99.9%', label: 'Uptime SLA' },
            { value: 'GDPR', label: 'Compliant' },
          ].map((s) => (
            <div key={`stat-${s.label}`} className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center">
              <p className="text-white font-700 text-sm">{s.value}</p>
              <p className="text-white/65 text-[11px] mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 lg:p-12 relative">
        {/* Top controls */}
        <div className="absolute top-4 right-4 flex items-center gap-2">
          <button
            onClick={() => onLangChange(lang === 'vi' ? 'en' : 'vi')}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-600 text-muted-foreground hover:bg-muted/60 hover:text-foreground transition-all duration-150 border border-border bg-card"
          >
            <Globe size={12} />
            {lang === 'vi' ? 'VI' : 'EN'}
          </button>
          <button
            onClick={() => onDarkModeChange(!darkMode)}
            className="p-2 rounded-xl text-muted-foreground hover:bg-muted/60 hover:text-foreground transition-all duration-150 border border-border bg-card"
          >
            {darkMode ? <Sun size={14} /> : <Moon size={14} />}
          </button>
        </div>

        <div className="w-full max-w-sm">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <Shield size={16} className="text-white" />
            </div>
            <span className="font-800 text-lg text-foreground">IRMVault</span>
          </div>

          {/* Header */}
          <div className="mb-6">
            <h2 className="text-2xl font-800 text-foreground mb-1">
              {lang === 'vi' ? 'Đăng nhập' : 'Sign In'}
            </h2>
            <p className="text-sm text-muted-foreground">{tr.domainOnly}</p>
          </div>

          {/* Error banner */}
          {loginError && (
            <div className="flex items-start gap-2 bg-destructive/10 border border-destructive/20 rounded-xl px-4 py-3 mb-5">
              <AlertTriangle size={15} className="text-destructive flex-shrink-0 mt-0.5" />
              <p className="text-xs text-destructive leading-relaxed">{loginError}</p>
            </div>
          )}

          {/* Google OAuth button */}
          <button
            type="button"
            onClick={handleGoogleLogin}
            disabled={isGoogleLoading || isLoggingIn}
            className="w-full py-3 text-sm font-600 text-foreground bg-card hover:bg-muted/60 border border-border rounded-xl transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2.5 mb-4 shadow-sm"
          >
            {isGoogleLoading ? (
              <><div className="w-4 h-4 border-2 border-muted-foreground/30 border-t-muted-foreground rounded-full animate-spin" />{tr.googleLoading}</>
            ) : (
              <><GoogleIcon size={18} />{tr.continueWithGoogle}</>
            )}
          </button>

          {/* Divider */}
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-1 h-px bg-border" />
            <span className="text-[11px] text-muted-foreground font-500">{tr.orDivider}</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          {/* Login form */}
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-xs font-600 text-muted-foreground mb-1.5">{tr.email}</label>
              <div className="relative">
                <Mail size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setEmailError(''); setLoginError(''); }}
                  placeholder={tr.emailPlaceholder}
                  autoComplete="email"
                  className="w-full pl-10 pr-4 py-3 rounded-xl border border-border bg-muted/30 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                />
              </div>
              {emailError && <p className="text-xs text-destructive mt-1.5">{emailError}</p>}
            </div>
            <div>
              <label className="block text-xs font-600 text-muted-foreground mb-1.5">{tr.password}</label>
              <div className="relative">
                <Key size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setPasswordError(''); setLoginError(''); }}
                  placeholder={tr.passwordPlaceholder}
                  autoComplete="current-password"
                  className="w-full pl-10 pr-11 py-3 rounded-xl border border-border bg-muted/30 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
              {passwordError && <p className="text-xs text-destructive mt-1.5">{passwordError}</p>}
            </div>
            <button
              type="submit"
              disabled={isLoggingIn || isGoogleLoading}
              className="w-full py-3 text-sm font-700 text-white bg-primary hover:bg-primary/90 rounded-xl transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 mt-2"
            >
              {isLoggingIn ? (
                <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />{tr.loggingIn}</>
              ) : (
                <><LogIn size={14} />{tr.loginBtn}</>
              )}
            </button>
          </form>

          <p className="text-[11px] text-muted-foreground text-center mt-5 leading-relaxed">
            {tr.adminNote}
          </p>

          <p className="text-[11px] text-muted-foreground text-center mt-1 leading-relaxed">
            {tr.domainRestrict}
          </p>

          {/* Guest mode */}
          <div className="mt-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-1 h-px bg-border" />
              <span className="text-[11px] text-muted-foreground font-500">{tr.orDivider}</span>
              <div className="flex-1 h-px bg-border" />
            </div>
            <button
              type="button"
              onClick={handleGuestMode}
              className="w-full py-2.5 text-sm font-600 text-muted-foreground hover:text-foreground border border-border hover:border-foreground/30 bg-muted/20 hover:bg-muted/50 rounded-xl transition-all flex items-center justify-center gap-2"
            >
              <UserCheck size={15} />
              {tr.guestMode}
            </button>
            <p className="text-[10px] text-muted-foreground text-center mt-2">{tr.guestModeDesc}</p>
          </div>
        </div>
      </div>
    </div>
  );
}